export const throttle = <F extends (...args: Parameters<F>) => ReturnType<F>>(
  func: F,
  delay: number,
  options?: { leading?: boolean },
) => {
  const { leading = true } = options ?? {};
  let timeoutId: ReturnType<typeof setTimeout> | null;
  let lastExecTime: number | null = null;

  const scheduleTrailingCall = (context: unknown, args: Parameters<F>, wait: number) => {
    clearTimeout(timeoutId!);
    timeoutId = setTimeout(() => {
      func.apply(context, args);
      lastExecTime = Date.now();
    }, wait);
  };

  const throttledFunction = function (this: unknown, ...args: Parameters<F>) {
    const currentTime = Date.now();

    if (lastExecTime === null) {
      if (leading) {
        func.apply(this, args);
      } else {
        scheduleTrailingCall(this, args, delay);
      }
      lastExecTime = currentTime;
    } else if (currentTime - lastExecTime >= delay) {
      func.apply(this, args);
      lastExecTime = currentTime;
    } else {
      scheduleTrailingCall(this, args, delay - (currentTime - lastExecTime));
    }
  };

  throttledFunction.cancel = () => {
    clearTimeout(timeoutId!);
    lastExecTime = null;
  };

  return throttledFunction;
};
